import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Flag, Plus, Trash2, CheckCircle2, Trophy, Users, ClipboardList,
  BarChart3, GitFork, UserCheck, Scissors, Save, ChevronDown, ChevronRight, Loader2,
} from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const GREEN = "#1B4D3E";
const CUT_SIZES = [16, 32, 64];

async function api(path, opts = {}) {
  const res = await fetch(`${API}${path}`, { credentials: "include", headers: AUTH(), ...opts });
  if (!res.ok) {
    let msg = "Error";
    try { msg = (await res.json()).detail || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  return res.json().catch(() => ({}));
}

const PART_STATUS = {
  registered: { txt: "Registrado", cls: "bg-gray-100 text-gray-600" },
  checked_in: { txt: "Presente", cls: "bg-blue-100 text-blue-700" },
  playing: { txt: "Jugando", cls: "bg-amber-100 text-amber-700" },
  finished: { txt: "Terminó", cls: "bg-green-100 text-green-700" },
};

export default function PuttingTab({ tournament, canWrite }) {
  const tid = tournament.tournament_id;
  const [putting, setPutting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState("participants");

  const load = useCallback(async () => {
    try {
      const p = await api(`/club/tournaments/${tid}/putting`);
      setPutting(p.exists ? p : null);
    } catch { setPutting(null); }
    finally { setLoading(false); }
  }, [tid]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#1B4D3E]" /></div>;

  if (!putting) return <SetupCard tid={tid} canWrite={canWrite} onCreated={load} />;

  const subs = [
    { k: "participants", l: "Participantes", icon: UserCheck },
    { k: "capture", l: "Captura", icon: ClipboardList },
    { k: "leaderboard", l: "Clasificación", icon: BarChart3 },
    { k: "bracket", l: "Llave", icon: GitFork },
  ];

  return (
    <div>
      <PuttingHeader putting={putting} canWrite={canWrite} onChange={load} tid={tid} />
      <div className="flex gap-1 my-4 overflow-x-auto" data-testid="putting-subtabs">
        {subs.map((s) => {
          const Icon = s.icon;
          return (
            <button key={s.k} onClick={() => setSub(s.k)}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${sub === s.k ? "bg-[#1B4D3E] text-white" : "text-[#64748B] hover:bg-gray-100"}`}
              data-testid={`putting-subtab-${s.k}`}>
              <Icon className="w-4 h-4" /> {s.l}
            </button>
          );
        })}
      </div>
      {sub === "participants" && <ParticipantsView tid={tid} putting={putting} canWrite={canWrite} onMutate={load} />}
      {sub === "capture" && <CaptureView tid={tid} putting={putting} canWrite={canWrite} onMutate={load} />}
      {sub === "leaderboard" && <LeaderboardView tid={tid} putting={putting} canWrite={canWrite} onCut={load} />}
      {sub === "bracket" && <BracketView tid={tid} putting={putting} canWrite={canWrite} onChange={load} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SetupCard({ tid, canWrite, onCreated }) {
  const [name, setName] = useState("Torneo de Putts");
  const [cutSize, setCutSize] = useState(32);
  const [matchFormat, setMatchFormat] = useState("strokes");
  const [useHandicap, setUseHandicap] = useState(false);
  const [pars, setPars] = useState(Array(9).fill(2));
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      await api(`/club/tournaments/${tid}/putting`, {
        method: "POST",
        body: JSON.stringify({ name, cut_size: cutSize, match_format: matchFormat, use_handicap: useHandicap, pars }),
      });
      toast.success("Torneo de putts creado");
      onCreated();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-1"><Flag className="w-5 h-5 text-[#1B4D3E]" /><h3 className="text-lg font-bold text-[#0F172A]">Torneo de Putts</h3></div>
      <p className="text-sm text-[#64748B] mb-5">Minitorneo de fantasía: 9 hoyos de putts, clasificación por golpes y eliminatorias directas.</p>
      {!canWrite ? <p className="text-sm text-gray-500">No configurado.</p> : (
        <div className="space-y-4 bg-white border rounded-xl p-5">
          <div>
            <label className="text-xs font-semibold text-[#64748B]">Nombre</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="putting-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748B]">Corte (clasificados)</label>
            <div className="flex gap-2 mt-1">
              {CUT_SIZES.map((c) => (
                <button key={c} onClick={() => setCutSize(c)} data-testid={`putting-cut-${c}`}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${cutSize === c ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "text-[#64748B] border-gray-200"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748B]">Formato de match</label>
            <div className="flex gap-2 mt-1">
              <button onClick={() => setMatchFormat("strokes")} data-testid="putting-fmt-strokes"
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${matchFormat === "strokes" ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "text-[#64748B] border-gray-200"}`}>Menos golpes</button>
              <button onClick={() => setMatchFormat("holes")} data-testid="putting-fmt-holes"
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${matchFormat === "holes" ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "text-[#64748B] border-gray-200"}`}>Hoyos ganados</button>
            </div>
          </div>
          <ParsEditor pars={pars} setPars={setPars} />
          <label className="flex items-center gap-2 text-sm text-[#0F172A]">
            <input type="checkbox" checked={useHandicap} onChange={(e) => setUseHandicap(e.target.checked)} data-testid="putting-usehcp" />
            Aplicar handicap en los matches
          </label>
          <Button onClick={create} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="putting-create-btn">
            {saving ? "Creando..." : "Crear torneo de putts"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ParsEditor({ pars, setPars }) {
  return (
    <div>
      <label className="text-xs font-semibold text-[#64748B]">Par por hoyo (9 hoyos)</label>
      <div className="grid grid-cols-9 gap-1 mt-1">
        {pars.map((p, i) => (
          <div key={i} className="text-center">
            <div className="text-[10px] text-gray-400">{i + 1}</div>
            <input type="number" min={1} max={6} value={p} data-testid={`putting-par-${i}`}
              onChange={(e) => { const v = [...pars]; v[i] = Math.max(1, Math.min(6, parseInt(e.target.value) || 1)); setPars(v); }}
              className="w-full text-center border rounded py-1 text-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}

function PuttingHeader({ putting, canWrite, onChange, tid }) {
  const del = async () => {
    if (!window.confirm("¿Eliminar el torneo de putts? Se borran participantes, tarjetas y llave.")) return;
    try { await api(`/club/tournaments/${tid}/putting`, { method: "DELETE" }); toast.success("Eliminado"); onChange(); }
    catch (e) { toast.error(e.message); }
  };
  const statusTxt = {
    classification: "Clasificación abierta", cut_done: "Corte hecho",
    bracket: "Eliminatorias", completed: "Finalizado", draft: "Borrador",
  }[putting.status] || putting.status;
  return (
    <div className="flex items-start justify-between flex-wrap gap-3 bg-[#1B4D3E]/5 border border-[#1B4D3E]/15 rounded-xl p-4">
      <div>
        <div className="flex items-center gap-2"><Flag className="w-5 h-5 text-[#1B4D3E]" /><h3 className="text-lg font-bold text-[#0F172A]">{putting.name}</h3></div>
        <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
          <span className="px-2 py-0.5 rounded-full bg-[#1B4D3E] text-white font-medium">{statusTxt}</span>
          <span className="text-[#64748B]">Corte top {putting.cut_size}</span>
          <span className="text-[#64748B]">· {putting.match_format === "holes" ? "Hoyos ganados" : "Menos golpes"}</span>
          <span className="text-[#64748B]">· {putting.participant_count || 0} inscritos · {putting.checked_in_count || 0} presentes</span>
          {putting.champion && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold flex items-center gap-1"><Trophy className="w-3 h-3" /> {putting.champion.name}</span>}
        </div>
      </div>
      {canWrite && <Button variant="outline" size="sm" onClick={del} className="text-red-600 border-red-200" data-testid="putting-delete-btn"><Trash2 className="w-3.5 h-3.5" /></Button>}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ParticipantsView({ tid, putting, canWrite, onMutate }) {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualName, setManualName] = useState("");
  const locked = ["cut_done", "bracket", "completed"].includes(putting.status);

  const load = useCallback(async () => {
    setLoading(true);
    try { setParts(await api(`/club/tournaments/${tid}/putting/participants`)); }
    finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onMutate && onMutate(); };

  const importFromTournament = async () => {
    try { const r = await api(`/club/tournaments/${tid}/putting/participants`, { method: "POST", body: JSON.stringify({ from_tournament: true }) }); toast.success(`${r.created} importados`); refresh(); }
    catch (e) { toast.error(e.message); }
  };
  const addManual = async () => {
    if (!manualName.trim()) return;
    try { await api(`/club/tournaments/${tid}/putting/participants`, { method: "POST", body: JSON.stringify({ players: [{ name: manualName.trim() }] }) }); setManualName(""); refresh(); }
    catch (e) { toast.error(e.message); }
  };
  const toggleCheckin = async (p) => {
    try { await api(`/club/tournaments/${tid}/putting/participants/${p.participant_id}/checkin`, { method: "POST", body: JSON.stringify({ checked_in: !p.checked_in }) }); refresh(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (p) => {
    try { await api(`/club/tournaments/${tid}/putting/participants/${p.participant_id}`, { method: "DELETE" }); refresh(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      {canWrite && !locked && (
        <div className="flex flex-wrap gap-2 mb-4">
          <Button size="sm" onClick={importFromTournament} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="putting-import-btn"><Users className="w-4 h-4 mr-1" /> Importar del torneo</Button>
          <div className="flex gap-2">
            <Input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Nombre..." className="h-9 w-48" data-testid="putting-manual-input" onKeyDown={(e) => e.key === "Enter" && addManual()} />
            <Button size="sm" variant="outline" onClick={addManual} data-testid="putting-add-manual-btn"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#1B4D3E]" /> : parts.length === 0 ? (
        <p className="text-sm text-gray-500">Sin participantes. Importa del torneo o agrega manualmente.</p>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          {parts.map((p) => {
            const st = PART_STATUS[p.status] || PART_STATUS.registered;
            return (
              <div key={p.participant_id} className="flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0" data-testid={`putting-part-${p.participant_id}`}>
                <span className="flex-1 text-sm font-medium text-[#0F172A]">{p.name}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.txt}</span>
                {canWrite && !locked && (
                  <>
                    <button onClick={() => toggleCheckin(p)} data-testid={`putting-checkin-${p.participant_id}`}
                      className={`text-xs px-2.5 py-1 rounded-lg font-medium flex items-center gap-1 ${p.checked_in ? "bg-blue-100 text-blue-700" : "border text-[#64748B]"}`}>
                      <UserCheck className="w-3.5 h-3.5" /> {p.checked_in ? "Presente" : "Check-in"}
                    </button>
                    <button onClick={() => remove(p)} className="text-red-500" data-testid={`putting-remove-${p.participant_id}`}><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function CaptureView({ tid, putting, canWrite, onMutate }) {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const locked = ["cut_done", "bracket", "completed"].includes(putting.status);

  const load = useCallback(async () => {
    setLoading(true);
    try { setParts(await api(`/club/tournaments/${tid}/putting/participants`)); }
    finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onMutate && onMutate(); };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#1B4D3E]" />;
  if (parts.length === 0) return <p className="text-sm text-gray-500">Agrega participantes primero.</p>;

  return (
    <div className="space-y-2">
      {locked && <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2">La clasificación está cerrada. Las tarjetas ya no son editables.</p>}
      {parts.map((p) => (
        <CardRow key={p.participant_id} tid={tid} putting={putting} p={p} canWrite={canWrite && !locked}
          open={openId === p.participant_id} onToggle={() => setOpenId(openId === p.participant_id ? null : p.participant_id)} onSaved={refresh} />
      ))}
    </div>
  );
}

function CardRow({ tid, putting, p, canWrite, open, onToggle, onSaved }) {
  const card = p.card || {};
  const [holes, setHoles] = useState(() => (card.holes || Array(9).fill(null)).map((h) => (h == null ? "" : String(h))));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setHoles((card.holes || Array(9).fill(null)).map((h) => (h == null ? "" : String(h)))); }, [p]); // eslint-disable-line

  const cardBadge = { pending: { t: "Pendiente", c: "bg-gray-100 text-gray-600" }, submitted: { t: "Entregada", c: "bg-blue-100 text-blue-700" }, validated: { t: "Validada", c: "bg-green-100 text-green-700" } }[card.status] || { t: "Pendiente", c: "bg-gray-100 text-gray-600" };

  const save = async (submit) => {
    setSaving(true);
    try {
      const payload = holes.map((h) => (h === "" ? null : parseInt(h)));
      await api(`/club/tournaments/${tid}/putting/participants/${p.participant_id}/card`, { method: "PUT", body: JSON.stringify({ holes: payload, submit }) });
      toast.success(submit ? "Tarjeta entregada" : "Guardado");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const validate = async (v) => {
    try { await api(`/club/tournaments/${tid}/putting/participants/${p.participant_id}/validate`, { method: "POST", body: JSON.stringify({ validate: v }) }); toast.success(v ? "Validada" : "Reabierta"); onSaved(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="border rounded-xl overflow-hidden" data-testid={`putting-cardrow-${p.participant_id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-2.5 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        <span className="flex-1 text-sm font-medium text-[#0F172A]">{p.name}</span>
        {card.total != null && <span className="text-sm font-bold text-[#1B4D3E]">{card.total}</span>}
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${cardBadge.c}`}>{cardBadge.t}</span>
      </button>
      {open && (
        <div className="px-4 pb-3 border-t pt-3 bg-gray-50/50">
          <div className="grid grid-cols-9 gap-1">
            {holes.map((h, i) => (
              <div key={i} className="text-center">
                <div className="text-[10px] text-gray-400">H{i + 1}<span className="text-gray-300"> · {putting.pars[i]}</span></div>
                <input type="number" min={1} max={15} value={h} disabled={!canWrite} data-testid={`putting-hole-${p.participant_id}-${i}`}
                  onChange={(e) => { const v = [...holes]; v[i] = e.target.value; setHoles(v); }}
                  className="w-full text-center border rounded py-1 text-sm disabled:bg-gray-100" />
              </div>
            ))}
          </div>
          {canWrite && (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving} data-testid={`putting-save-card-${p.participant_id}`}><Save className="w-3.5 h-3.5 mr-1" /> Guardar</Button>
              <Button size="sm" onClick={() => save(true)} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid={`putting-submit-card-${p.participant_id}`}>Entregar</Button>
              {card.status === "submitted" && <Button size="sm" onClick={() => validate(true)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`putting-validate-${p.participant_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Validar</Button>}
              {card.status === "validated" && <Button size="sm" variant="outline" onClick={() => validate(false)} data-testid={`putting-unvalidate-${p.participant_id}`}>Reabrir</Button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function LeaderboardView({ tid, putting, canWrite, onCut }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cutting, setCutting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api(`/club/tournaments/${tid}/putting/leaderboard`)); }
    finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const confirmCut = async () => {
    if (!window.confirm(`¿Confirmar el corte a los mejores ${putting.cut_size}? Se generará la llave de eliminatorias.`)) return;
    setCutting(true);
    try { const r = await api(`/club/tournaments/${tid}/putting/cut`, { method: "POST" }); toast.success(`Corte hecho: ${r.qualified} clasificados`); onCut(); }
    catch (e) { toast.error(e.message); }
    finally { setCutting(false); }
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#1B4D3E]" />;
  const entries = data?.entries || [];
  const ranked = entries.filter((e) => e.total != null);
  const canCut = canWrite && putting.status === "classification";

  return (
    <div>
      {canCut && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
          <div className="text-sm text-amber-800">Corte automático a top {putting.cut_size} (desempate por conteo regresivo).</div>
          <Button size="sm" onClick={confirmCut} disabled={cutting || ranked.length < 2} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="putting-cut-btn">
            <Scissors className="w-4 h-4 mr-1" /> {cutting ? "Generando..." : "Confirmar corte"}
          </Button>
        </div>
      )}
      {entries.length === 0 ? <p className="text-sm text-gray-500">Sin tarjetas capturadas.</p> : (
        <div className="border rounded-xl overflow-hidden">
          <div className="grid grid-cols-[40px_1fr_60px_70px] gap-2 px-4 py-2 bg-gray-50 text-[11px] font-semibold text-[#64748B] uppercase">
            <span>Pos</span><span>Jugador</span><span className="text-center">Total</span><span className="text-right">Estado</span>
          </div>
          {entries.map((e, i) => {
            const isCutLine = e.made_cut && (i + 1 === putting.cut_size);
            return (
              <div key={e.participant_id} data-testid={`putting-lb-${e.participant_id}`}
                className={`grid grid-cols-[40px_1fr_60px_70px] gap-2 px-4 py-2 items-center text-sm border-b last:border-b-0 ${e.made_cut ? "bg-green-50/40" : ""} ${isCutLine ? "border-b-2 border-b-amber-400" : ""}`}>
                <span className="font-bold text-[#1B4D3E]">{e.position_label}</span>
                <span className="text-[#0F172A]">{e.name}{e.validated && <CheckCircle2 className="inline w-3 h-3 ml-1 text-green-600" />}</span>
                <span className="text-center font-bold">{e.total ?? "—"}</span>
                <span className="text-right">{e.made_cut ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Clasifica</span> : <span className="text-[11px] text-gray-400">{e.card_status === "validated" ? "Fuera" : "Prov."}</span>}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function BracketView({ tid, putting, canWrite, onChange }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(putting.status);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api(`/club/tournaments/${tid}/putting/bracket`); setMatches(r.matches || []); setStatus(r.status); }
    finally { setLoading(false); }
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const finalizeTournament = async () => {
    try { const r = await api(`/club/tournaments/${tid}/putting/finalize`, { method: "POST" }); toast.success(`Campeón: ${r.champion.name}`); onChange(); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#1B4D3E]" />;
  if (matches.length === 0) return <p className="text-sm text-gray-500">La llave se genera al confirmar el corte en la pestaña Clasificación.</p>;

  const rounds = {};
  matches.forEach((m) => { (rounds[m.round_index] = rounds[m.round_index] || []).push(m); });
  const roundKeys = Object.keys(rounds).sort((a, b) => a - b);
  const final = matches.find((m) => m.next_match_id == null);
  const canFinalize = canWrite && final?.winner_participant_id && putting.status !== "completed";

  return (
    <div>
      {canFinalize && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
          <div className="text-sm text-amber-800">La final tiene ganador. Publica al campeón.</div>
          <Button size="sm" onClick={finalizeTournament} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="putting-finalize-btn"><Trophy className="w-4 h-4 mr-1" /> Coronar campeón</Button>
        </div>
      )}
      <div className="flex gap-4 overflow-x-auto pb-2">
        {roundKeys.map((rk) => (
          <div key={rk} className="min-w-[240px] flex-shrink-0">
            <div className="text-xs font-bold text-[#1B4D3E] uppercase mb-2">{rounds[rk][0].round_name}</div>
            <div className="space-y-2">
              {rounds[rk].map((m) => <MatchCard key={m.match_id} tid={tid} putting={putting} m={m} canWrite={canWrite} onSaved={() => { load(); onChange(); }} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchCard({ tid, putting, m, canWrite, onSaved }) {
  const [open, setOpen] = useState(false);
  const [ca, setCa] = useState(() => (m.card_a || Array(9).fill(null)).map((h) => (h == null ? "" : String(h))));
  const [cb, setCb] = useState(() => (m.card_b || Array(9).fill(null)).map((h) => (h == null ? "" : String(h))));
  const [saving, setSaving] = useState(false);
  const bothPresent = m.slot_a && m.slot_b;
  const winnerA = m.winner_participant_id && m.slot_a && m.winner_participant_id === m.slot_a.participant_id;
  const winnerB = m.winner_participant_id && m.slot_b && m.winner_participant_id === m.slot_b.participant_id;

  const save = async (finalize) => {
    setSaving(true);
    try {
      await api(`/club/tournaments/${tid}/putting/matches/${m.match_id}`, {
        method: "PUT",
        body: JSON.stringify({ card_a: ca.map((h) => (h === "" ? null : parseInt(h))), card_b: cb.map((h) => (h === "" ? null : parseInt(h))), finalize }),
      });
      toast.success(finalize ? "Match cerrado" : "Guardado");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const slotRow = (slot, isWinner, total, holesWon) => (
    <div className={`flex items-center gap-1.5 text-sm ${isWinner ? "font-bold text-[#1B4D3E]" : "text-[#0F172A]"}`}>
      {slot?.seed && <span className="text-[10px] text-gray-400 w-4">{slot.seed}</span>}
      <span className="flex-1 truncate">{slot ? slot.name : <span className="text-gray-300 italic">Bye</span>}</span>
      {putting.match_format === "holes" && holesWon != null && <span className="text-xs text-gray-500">{holesWon}h</span>}
      {total != null && <span className="text-xs font-bold">{total}</span>}
      {isWinner && <Trophy className="w-3 h-3 text-amber-500" />}
    </div>
  );

  return (
    <div className={`border rounded-xl p-2.5 bg-white ${m.status === "completed" ? "border-green-200" : ""}`} data-testid={`putting-match-${m.match_id}`}>
      <div className="space-y-1">
        {slotRow(m.slot_a, winnerA, m.total_a, m.holes_won_a)}
        <div className="border-t border-dashed" />
        {slotRow(m.slot_b, winnerB, m.total_b, m.holes_won_b)}
      </div>
      {canWrite && bothPresent && m.status !== "completed" && (
        <button onClick={() => setOpen(!open)} className="text-xs text-[#1B4D3E] font-medium mt-2 flex items-center gap-1" data-testid={`putting-match-toggle-${m.match_id}`}>
          <ClipboardList className="w-3 h-3" /> {open ? "Cerrar" : "Capturar"}
        </button>
      )}
      {open && (
        <div className="mt-2 space-y-2">
          {[["A", ca, setCa, m.slot_a], ["B", cb, setCb, m.slot_b]].map(([lbl, arr, setArr, slot]) => (
            <div key={lbl}>
              <div className="text-[10px] font-semibold text-[#64748B] truncate">{slot?.name}</div>
              <div className="grid grid-cols-9 gap-0.5">
                {arr.map((h, i) => (
                  <input key={i} type="number" min={1} max={15} value={h} data-testid={`putting-match-${m.match_id}-${lbl}-${i}`}
                    onChange={(e) => { const v = [...arr]; v[i] = e.target.value; setArr(v); }}
                    className="w-full text-center border rounded py-0.5 text-xs" />
                ))}
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving} className="text-xs h-7" data-testid={`putting-match-save-${m.match_id}`}>Guardar</Button>
            <Button size="sm" onClick={() => save(true)} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white text-xs h-7" data-testid={`putting-match-finalize-${m.match_id}`}>Cerrar match</Button>
          </div>
        </div>
      )}
    </div>
  );
}
